import { PlayerData, ResourceCounts } from '../server/models';

/**
 * Interface for defining requirements that must be met for game actions
 */
export interface Requirement<T> {
  /** Unique identifier for this requirement */
  id: string;
  
  /** User-friendly description of this requirement */
  description: string;
  
  /** Function that checks if the requirement is met */
  isMet: (state: T) => boolean;
}

/**
 * Utility function to check if all requirements are met
 */
export function checkRequirements<T>(requirements: Requirement<T>[], state: T): { met: boolean; unmetRequirements: Requirement<T>[] } {
  const unmetRequirements = requirements.filter(req => !req.isMet(state));
  return {
    met: unmetRequirements.length === 0,
    unmetRequirements
  };
}

/**
 * Interface for membrane expansion stage configuration
 */
export interface MembraneExpansionStage {
  /** Stage number (1-4) */
  stage: number;
  
  /** Size multiplier for this stage (e.g., 1.0, 1.15, 1.3, 1.5) */
  sizeMultiplier: number;
  
  /** Speed multiplier for this stage (e.g., 1.0, 0.95, 0.85, 0.7) */
  speedMultiplier: number;
  
  /** Cost in lipids to upgrade to this stage */
  lipidCost: number;
  
  /** Cost in ATP/energy to upgrade to this stage */
  atpCost: number;
  
  /** Requirements that must be met to achieve this stage */
  requirements: Requirement<PlayerData>[];
}

/**
 * Factory for creating common requirements
 */
export const RequirementFactory = {
  /**
   * Creates a requirement for a minimum number of a specific organelle type
   */
  minOrganelleCount(type: string, count: number): Requirement<PlayerData> {
    // Format the organelle name for display purposes
    let displayName = type;
    if (type === 'mitochondrion') displayName = 'Mitochondria';
    else if (type === 'lysosome') displayName = 'Lysosomes';
    else if (type === 'peroxisome') displayName = 'Peroxisomes';
    else if (count !== 1) displayName = type + 's'; // Basic pluralization
    
    return {
      id: `min-${type}-${count}`,
      description: `At least ${count}x ${displayName}`,
      isMet: (state: PlayerData) => {
        const organelleCount = state.organelles.filter(o => o.type === type).length;
        return organelleCount >= count;
      }
    };
  },

  /**
   * Creates a requirement for a minimum amount of a specific resource
   */
  minResourceAmount(resource: keyof ResourceCounts, amount: number): Requirement<PlayerData> {
    // Format the resource name for display purposes
    let displayName = resource.toString();
    if (resource === 'lipids') displayName = 'Lipids';
    else if (resource === 'glucose') displayName = 'Glucose';
    else if (resource === 'atp') displayName = 'ATP';
    
    return {
      id: `min-${resource}-${amount}`,
      description: `At least ${amount} ${displayName}`,
      isMet: (state: PlayerData) => {
        const resourceAmount = state.resources[resource] as number || 0;
        return resourceAmount >= amount;
      }
    };
  }
};

/**
 * Configuration for all membrane expansion stages
 */
export const MEMBRANE_EXPANSION_STAGES: MembraneExpansionStage[] = [
  // Stage 1: Default starting stage
  {
    stage: 1,
    sizeMultiplier: 1.0,
    speedMultiplier: 1.0,
    lipidCost: 0,
    atpCost: 0,
    requirements: [] // No requirements for starting stage
  },
  
  // Stage 2: First expansion
  {
    stage: 2,
    sizeMultiplier: 1.15,
    speedMultiplier: 0.95,
    lipidCost: 100,
    atpCost: 0,
    requirements: [
      RequirementFactory.minOrganelleCount('mitochondrion', 3),
      RequirementFactory.minOrganelleCount('lysosome', 1)
    ]
  },
  
  // Stage 3: Second expansion
  {
    stage: 3,
    sizeMultiplier: 1.3,
    speedMultiplier: 0.85,
    lipidCost: 200,
    atpCost: 30,
    requirements: [
      RequirementFactory.minOrganelleCount('mitochondrion', 6),
      RequirementFactory.minOrganelleCount('lysosome', 3)
    ]
  },
  
  // Stage 4: Final expansion
  {
    stage: 4,
    sizeMultiplier: 1.5,
    speedMultiplier: 0.7,
    lipidCost: 300,
    atpCost: 50,
    requirements: [
      RequirementFactory.minOrganelleCount('mitochondrion', 10),
      RequirementFactory.minOrganelleCount('lysosome', 5),
      RequirementFactory.minOrganelleCount('peroxisome', 3)
    ]
  }
];

/**
 * Get expansion stage by ID
 */
export function getExpansionStageById(id: number): MembraneExpansionStage | undefined {
  return MEMBRANE_EXPANSION_STAGES.find(stage => stage.stage === id);
}

/**
 * Get the next expansion stage from the current one
 */
export function getNextExpansionStage(currentStage: number): MembraneExpansionStage | undefined {
  return MEMBRANE_EXPANSION_STAGES.find(stage => stage.stage === currentStage + 1);
} 